import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { SelectableNavigationItem } from '../../model/selectable-navigation-item';
import { Router } from '@angular/router';
import { NavigationItem } from '../../model/navigation-item';
import { OmapiIdConstants } from '../../shared/omapi-id-constants';
import { PriceUtils } from '../../shared/utils/price/price.utils';
import { LanguageService } from '../../services/language/language.service';
import { TvshopConstants } from '../../shared/tvshop-constants';
import { Environment } from '../../shared/environment/environment';
import { NavigationService } from '../../services/navigation/navigation.service';
import { PageNames } from '../../shared/page-names.enum';
import { CarouselService } from '../../services/carousel/carousel.service';
import { takeUntil } from 'rxjs/operators';
import { TvShopEntertainmentPack } from '../../model/tv-shop-entertainment-pack';
import { OmapiIdsUtils } from '../../shared/utils/omapi-ids/omapi-ids-utils';
import { TvShopStateService } from '../../services/state/tv-shop-state.service';

@Component({
  selector: 'app-carousel-item',
  templateUrl: './carousel-item.component.html',
  styleUrls: ['./carousel-item.component.less']
})
export class CarouselItemComponent extends SelectableNavigationItem implements OnInit, OnDestroy {
  @Input() omapiId;
  @Input() pack: TvShopEntertainmentPack;
  @Input() isBigTile: boolean;
  @Input() showSubtitle: boolean;
  @Input() showPromoBadge: boolean;
  @Output()
  selectedCarouselItem: EventEmitter<NavigationItem> = new EventEmitter<NavigationItem>();
  @ViewChild('carouselImageWrapper') carouselImageWrapper: ElementRef;

  showStreamzPromoBadge: boolean;

  constructor(private router: Router,
              private navigationService: NavigationService,
              private changeDetector: ChangeDetectorRef,
              private languageService: LanguageService,
              private renderer: Renderer2,
              private carouselService: CarouselService,
              private stateService: TvShopStateService
  ) {
    super();
  }

  ngOnInit() {
    this.navigationService.registerNavigation(this, PageNames.HOMEPAGE);
    this.appendInMemoryImage();
    this.shouldShowStreamzPromoBadge();
  }

  private appendInMemoryImage(): void {
    this.carouselService.carouselImages$.pipe(takeUntil(this.stopSubscription)).subscribe((imageArray: Array<any>) => {
      if (imageArray && this.carouselImageWrapper && this.omapiId) {
        this.renderer.appendChild(this.carouselImageWrapper.nativeElement, imageArray[this.omapiId + '-item']);
      }
    });
  }

  activate(active: boolean): void {
    this.active = active;
    if (this.active) {
      this.selectedCarouselItem.emit(this);
    }
    this.changeDetector.detectChanges();
  }

  onBack(): void {
    window.close();
  }

  onEnter(): void {
    this.stateService.setSelectedTvShopEntertainmentPack(this.pack);
    this.router.navigate(['/product-details/', this.omapiId], {queryParamsHandling: 'merge'});
  }

  shouldShowStreamzPromoBadge(): void {
    this.showStreamzPromoBadge = OmapiIdsUtils.STREAMZ_FREE_ADD_PACKS_GROUP.includes(this.pack.omapiId);
  }

  get isTrialProduct(): boolean {
    return this.pack.omapiId === OmapiIdConstants.TRIALS;
  }

  get hasPromo(): boolean {
    return this.pack && this.pack.priceDetail && this.pack.priceDetail.hasPromo();
  }

  get textIntegerPart(): string {
    return this.pack && this.pack.priceDetail ? this.pack.priceDetail.price.integerPart() : undefined;
  }

  get textDecimalPart(): string {
    return this.getDecimalTextPart(this.pack && this.pack.priceDetail ? this.pack.priceDetail.price.decimalPart() : undefined);
  }

  get promoTextIntegerPrice(): string {
    return this.pack && this.pack.priceDetail ? this.pack.priceDetail.promoPrice.integerPart() : undefined;
  }

  get promoTextDecimalPart(): string {
    return this.getDecimalTextPart(this.pack && this.pack.priceDetail ? this.pack.priceDetail.promoPrice.decimalPart() : undefined);
  }

  get promoLabel(): string {
    return this.pack && this.pack.priceDetail ? this.pack.priceDetail.promoLabel : undefined;
  }

  get promoDuration(): number {
    return this.pack && this.pack.priceDetail ? this.pack.priceDetail.promoDuration : undefined;
  }

  public getDecimalTextPart(price: string): string {
    return PriceUtils.prefixDecimalPart(price, this.currentLang);
  }

  get currentLang(): string {
    return this.languageService.getCurrentLanguage().toLowerCase();
  }

  get isSizeOneClassNeeded(): boolean {
    const logosNeedSizeOneClass: string[] = [
      OmapiIdsUtils.DISNEY_PLUS_ID,
      OmapiIdsUtils.BOUQUET_AFRICAIN_LEGACY_ID,
      OmapiIdsUtils.FAMILY_FUN_LEGACY_ID,
      OmapiIdsUtils.PASSION_XL_LEGACY_ID,
      OmapiIdsUtils.DISCOVER_MORE_LEGACY_ID,
      OmapiIdsUtils.TRIALS_LEGACY_ID
    ];

    const omapiId = this.pack && this.pack.omapiId || '';
    return logosNeedSizeOneClass.map(o => o.toLowerCase()).includes(OmapiIdsUtils.getCorrectIdForResource(omapiId).toLowerCase());
  }

  public get logoUrl(): string {
    const baseUrl = Environment.environmentConfig() && Environment.environmentConfig().baseUrl;
    const url = baseUrl + TvshopConstants.CAROUSEL_LOGOS_AEM_PATH + OmapiIdsUtils.getCorrectIdForResource(this.pack.omapiId);
    const logoPostfix = this.isTrialProduct ? 'visual.png' : 'logo.png';

    return `${url}-carousel-${logoPostfix}`;
  }

  public isRedCarpetOrRedCarpetMore(): boolean {
    return (this.pack.omapiId === OmapiIdConstants.STREAMZ || this.pack.omapiId === OmapiIdConstants.RED_CARPET_MORE);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.carouselImageWrapper) {
      this.carouselImageWrapper.nativeElement = null;
    }
    this.carouselImageWrapper = null;
  }
}
